import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Star, User, Trophy, Award, Medal } from "lucide-react";
import * as Chart from "chart.js";
import { Link } from "react-router-dom";

// Register Chart.js components
Chart.Chart.register(
  Chart.CategoryScale,
  Chart.LinearScale,
  Chart.BarElement,
  Chart.Title,
  Chart.Tooltip,
  Chart.Legend,
);

// Type definitions
interface DriverData {
  user_id: string;
  fullName: string;
  profile_picture: string | null;
  rating: string;
  total_revenue: string;
  total_rides: string;
}

interface BarColorConfig {
  background: string;
  solid: string;
}

interface AvatarPosition {
  left: number;
  top: number;
  canvasTop: number;
  driver: DriverData;
  index: number;
}

interface TopDriversChartProps {
  drivers: DriverData[] | { drivers: DriverData[] }; // Accept both formats
  barColor: string;
}

interface TooltipContext {
  dataIndex: number;
}

interface ChartTooltipItem {
  dataIndex: number;
}

const TopDriversChart: React.FC<TopDriversChartProps> = ({
  drivers,
  barColor,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart.Chart | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [avatarPositions, setAvatarPositions] = useState<AvatarPosition[]>([]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // console.log("TopDriversChart 〽️〽️〽️〽️〽️〽️:", drivers);

  const safeDrivers = useMemo(() => {
    const getProcessedDrivers = (
      data: { drivers: DriverData[] } | DriverData[],
    ): DriverData[] => {
      if (!data) return [];

      let rawDrivers: DriverData[] = [];

      // Extract the drivers array
      if (
        typeof data === "object" &&
        "drivers" in data &&
        Array.isArray(data.drivers)
      ) {
        rawDrivers = data.drivers;
      } else if (Array.isArray(data)) {
        rawDrivers = data;
      }

      // Transform the data to match our interface
      return rawDrivers.map(
        (driver: DriverData): DriverData => ({
          user_id: driver.user_id || "",
          fullName: driver.fullName || "Unknown Driver",
          profile_picture: driver.profile_picture || "",
          rating: driver.rating || "0",
          total_revenue: driver.total_revenue || "0",
          total_rides: driver.total_rides || "0",
        }),
      );
    };

    return getProcessedDrivers(drivers);
  }, [drivers]);

  const barColors = useMemo(() => {
    const getBarColors = (drivers: DriverData[]): BarColorConfig[] => {
      return drivers.map((_, index) => {
        if (index === 0)
          return {
            background: "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            solid: "#FFD700",
          };
        if (index === 1)
          return {
            background: "linear-gradient(135deg, #C0C0C0 0%, #A8A8A8 100%)",
            solid: "#C0C0C0",
          };
        if (index === 2)
          return {
            background: "linear-gradient(135deg, #CD7F32 0%, #B8860B 100%)",
            solid: "#CD7F32",
          };
        return {
          background: `linear-gradient(135deg, ${barColor} 0%, ${barColor}CC 100%)`,
          solid: barColor,
        };
      });
    };

    return getBarColors(safeDrivers);
  }, [safeDrivers, barColor]);

  const getRankIcon = (index: number): JSX.Element | null => {
    if (index === 0)
      return <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500" />;
    if (index === 1)
      return <Award className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />;
    if (index === 2)
      return <Medal className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-700" />;
    return null;
  };

  const updateAvatarPositions = useCallback((): void => {
    if (!chartRef.current || !containerRef.current) return;

    const chart = chartRef.current;
    const positions: AvatarPosition[] = [];

    safeDrivers.forEach((driver, index) => {
      const meta = chart.getDatasetMeta(0);
      const bar = meta.data[index];

      if (bar) {
        const canvasRect = chart.canvas.getBoundingClientRect();
        const containerRect = containerRef.current!.getBoundingClientRect();

        positions.push({
          left: bar.x - (isMobile ? 20 : 30),
          top: bar.y - (isMobile ? 50 : 40),
          canvasTop: canvasRect.top - containerRect.top,
          driver: driver,
          index: index,
        });
      }
    });

    setAvatarPositions(positions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeDrivers]);

  // const handleImageError = (
  //   e: React.SyntheticEvent<HTMLImageElement>,
  // ): void => {
  //   const target = e.target as HTMLImageElement;
  //   target.style.display = "none";
  //   const nextSibling = target.nextSibling as HTMLElement;
  //   if (nextSibling) {
  //     nextSibling.style.display = "flex";
  //   }
  // };

  useEffect(() => {
    // Early return if no drivers data
    if (!canvasRef.current || safeDrivers.length === 0) return;

    // Destroy existing chart
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const gradients = safeDrivers.map((_, index) => {
      const g = ctx.createLinearGradient(0, 0, 0, 400);

      if (index === 0) {
        g.addColorStop(0, "#FFD700");
        g.addColorStop(1, "#F59E0B");
      } else if (index === 1) {
        g.addColorStop(0, "#C0C0C0");
        g.addColorStop(1, "#9CA3AF");
      } else if (index === 2) {
        g.addColorStop(0, "#CD7F32");
        g.addColorStop(1, "#B45309");
      } else {
        g.addColorStop(0, "#2caaff");
        g.addColorStop(1, "#B8DAF4");
      }

      return g;
    });

    chartRef.current = new Chart.Chart(ctx, {
      type: "bar",
      data: {
        labels: safeDrivers.map(() => ""),
        datasets: [
          {
            label: "Courses",
            data: safeDrivers.map((driver) => Number(driver.total_rides)),
            backgroundColor: gradients,
            borderColor: barColors.map((color) => color.solid),
            borderWidth: 0,
            borderRadius: 30,
            borderSkipped: false,
            barThickness: isMobile ? 35 : 70,
            categoryPercentage: 0.5,
            barPercentage: 0.9,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 50,
            bottom: 10,
            left: 5,
            right: 5,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          datalabels: {
            color: "#000000",
            anchor: "center",
            align: "center",
            font: {
              weight: "bold",
              size: 12,
            },
            formatter: (value: number) => value,
          },
          tooltip: {
            backgroundColor: "rgba(17, 24, 39, 0.95)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "rgba(255, 255, 255, 0.1)",
            borderWidth: 1,
            cornerRadius: 12,
            bodySpacing: 5,
            displayColors: false,
            titleFont: {
              size: 12,
              weight: "bold",
            },
            bodyFont: {
              size: 11,
            },
            padding: 10,
            position: "average",
            yAlign: "bottom",
            xAlign: "center",

            callbacks: {
              title: function (context: ChartTooltipItem[]): string {
                const index = context[0].dataIndex;
                return `🏆 ${safeDrivers[index].fullName}`;
              },
              label: function (context: TooltipContext): string[] {
                const index = context.dataIndex;
                const driver = safeDrivers[index];
                return [
                  `🚗 ${driver.total_rides} courses terminées`,
                  `💰 ${Number(driver.total_revenue).toLocaleString()} DH de revenus`,
                  `⭐ ${driver.rating}/5.0 étoiles`,
                  `🥇 Position: ${index + 1}${index === 0 ? "ère" : "ème"}`,
                ];
              },
            },
          },
        },
        scales: {
          x: {
            display: false,
            grid: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(156, 163, 175, 0.2)",
              lineWidth: 1,
            },
            ticks: {
              color: "#6B7280",
              font: {
                size: 10,
              },
              padding: 5,
              callback: function (value: any): string {
                return value.toString();
              },
            },
          },
        },
        animation: {
          duration: 100,
          easing: "easeOutQuart",
          onComplete: () => {
            setTimeout(updateAvatarPositions, 100);
          },
        },
        onResize: () => {
          setTimeout(updateAvatarPositions, 100);
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
      },
    });

    // Initial position update
    setTimeout(updateAvatarPositions, 100);

    // Cleanup function
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [safeDrivers, barColors, updateAvatarPositions]);

  return (
    <div
      className={`${isMobile ? "p-0" : "p-3"} container mx-auto sm:p-6 lg:p-8 bg-gradient-to-br from-white to-gray-50 rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl`}
    >
      {/* Chart Container with Floating Avatars */}
      <div ref={containerRef} className="relative mb-4 sm:mb-6 lg:mb-8">
        <div className="relative h-48 sm:h-64 lg:h-80">
          <canvas ref={canvasRef} className="w-full h-full" />

          {/* Floating Avatars */}
          {avatarPositions.map((pos, index) => (
            <div
              key={index}
              className="absolute transition-all duration-300 hover:scale-110 cursor-pointer"
              style={{
                left: `${pos.left}px`,
                top: `${pos.top + pos.canvasTop}px`,
                zIndex: 20,
                marginTop: "40px",
              }}
            >
              {/* Avatar Container */}
              <div className="relative group">
                <div
                  className="relative rounded-full p-1 shadow-lg sm:shadow-xl"
                  style={{
                    width: isMobile ? "40px" : "60px",
                    height: isMobile ? "40px" : "60px",
                    zIndex: 2,
                  }}
                >
                  <div className="w-full h-full rounded-full overflow-hidden">
                    {pos.driver?.profile_picture ? (
                      <Link
                        to={`/drivers/${pos.driver?.user_id}`}
                        className="fw-bold m-0"
                      >
                        <img
                          src={pos.driver?.profile_picture}
                          alt=""
                          width={"100%"}
                          height={"100%"}
                          style={{ borderRadius: "100%" }}
                        />
                      </Link>
                    ) : (
                      <Link
                        to={`/drivers/${pos.driver?.user_id}`}
                        className="fw-bold m-0"
                      >
                        <User className="w-full h-full text-black" />
                      </Link>
                    )}
                  </div>
                </div>

                {/* Rank Badge  */}
                <div
                  className="absolute  w-3 h-3 sm:w-3.5 sm:h-3.5 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-100 "
                  style={{
                    top: "-15px",
                    right: "-20%",
                    transform: "translateX(-50%)",
                    zIndex: 3,
                  }}
                >
                  {getRankIcon(pos.index) || null}
                </div>

                {/* Rating Badge - Positioned below avatar */}
                <div
                  className={`absolute flex items-center gap-2 text-white ${isMobile ? "px-2" : "px-2"}  py-0.5 rounded-pill shadow-lg `}
                  style={{
                    fontSize: isMobile ? "8px" : "16px",
                    backgroundColor: "#f97722",
                    zIndex: 3,
                    minWidth: isMobile ? "30px" : "40px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Star
                    className="fill-current flex-shrink-0"
                    style={{
                      width: isMobile ? "14px" : "20px",
                      height: isMobile ? "14px" : "20px",
                    }}
                  />
                  <span>{pos.driver.rating}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopDriversChart;
